import { ForecastFolder } from "../models/UserArpsModel";

export interface FolderSearchResult {
  filteredFolders: ForecastFolder[];
  expandedKeys: string[];
}

export function searchForecastFolders(
  folders: ForecastFolder[],
  searchValue: string
): FolderSearchResult {
  if (!searchValue || searchValue.length === 0) {
    return {
      filteredFolders: folders,
      expandedKeys: []
    };
  }

  const lowerSearchValue = searchValue.toLowerCase();

  const folderMap = new Map<string, ForecastFolder>();
  folders.forEach((f) => folderMap.set(f.folderId, f));

  const foldersWithDirectMatches: ForecastFolder[] = [];
  const allRequiredFolderIds = new Set<string>();

  for (const folder of folders) {
    // Check if folder name matches search value
    const folderNameMatches =
      folder.title.toLowerCase().includes(lowerSearchValue) ||
      folder.folderName.toLowerCase().includes(lowerSearchValue);

    // Filter children that match the search
    const matchingChildren = folder.children.filter((child) =>
      child.title.toLowerCase().includes(lowerSearchValue)
    );

    // Include the folder if it has matches or folder name matches
    if (matchingChildren.length > 0 || folderNameMatches) {
      // Create filtered folder with matching children (or all children if folder name matches)
      const filteredFolder = {
        ...folder,
        children: folderNameMatches ? folder.children : matchingChildren
      };

      foldersWithDirectMatches.push(filteredFolder);

      // Add this folder and all its parents to required set
      allRequiredFolderIds.add(folder.key);
      const parentIds = getAllParentFolderIds(folder.key, folderMap);
      parentIds.forEach((parentId) => allRequiredFolderIds.add(parentId));
    }
  }

  // Build final filtered array with required folders
  const filteredFolders: ForecastFolder[] = [];
  for (const folder of folders) {
    if (allRequiredFolderIds.has(folder.key)) {
      // Check if this folder has direct matches
      const folderWithMatches = foldersWithDirectMatches.find(
        (fm) => fm.key === folder.key
      );
      if (folderWithMatches) {
        // Use the folder with filtered children
        filteredFolders.push(folderWithMatches);
      } else {
        // This is a parent folder, include it with empty children array
        filteredFolders.push({
          ...folder,
          children: []
        });
      }
    }
  }
  return {
    filteredFolders,
    expandedKeys: Array.from(allRequiredFolderIds)
  };
}

function getAllParentFolderIds(
  folderId: string,
  folderMap: Map<string, ForecastFolder>
): string[] {
  const parentIds = [];
  let currentFolderId = folderId;

  while (currentFolderId) {
    const folder = folderMap.get(currentFolderId);
    if (!folder || !folder.parentId || folder.parentId === "") {
      break;
    }
    parentIds.push(folder.parentId);
    currentFolderId = folder.parentId;
  }

  return parentIds;
}
